import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useOutletContext, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CalendarDays, ClipboardCheck, Wallet, Baby, MessageCircle, Shield, UserCog, Loader2, Check, AlertCircle, Award, FileText, Download } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { toast } from "sonner";
import type { PortalCtx } from "./PortalLayout";
import { fmtDateTime, fmtDate } from "@/utils/datetime";

type Tab = "overview" | "attendance" | "finance" | "kg" | "certs" | "docs" | "consents" | "profile";

function formatMoney(minor: number, currency: string, lang: string) {
    return new Intl.NumberFormat(lang, { style: "currency", currency }).format(minor / 100);
}

function StatusPill({ status, t }: { status: string; t: any }) {
    const map: Record<string, { bg: string; fg: string; icon: ReactElement; label: string }> = {
        present: { bg: "var(--success-bg)", fg: "var(--success-fg)", icon: <Check size={12} />, label: t("attendance.status.present") },
        late: { bg: "var(--warning-bg)", fg: "var(--warning-fg)", icon: <AlertCircle size={12} />, label: t("attendance.status.late") },
        excused_absence: { bg: "var(--info-bg)", fg: "var(--info-fg)", icon: <AlertCircle size={12} />, label: t("attendance.status.excused_absence") },
        unexcused_absence: { bg: "var(--error-bg)", fg: "var(--error-fg)", icon: <AlertCircle size={12} />, label: t("attendance.status.unexcused_absence") },
    };
    const it = map[status] || { bg: "var(--input)", fg: "var(--text)", icon: <></>, label: status };
    return (
        <span className="inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5" style={{ background: it.bg, color: it.fg }}>
            {it.icon}{it.label}
        </span>
    );
}

export default function PortalHome() {
    const { t, i18n } = useTranslation();
    const ctx = useOutletContext<PortalCtx>();
    const [tab, setTab] = useState<Tab>("overview");
    const [sessions, setSessions] = useState<any[]>([]);
    const [attendance, setAttendance] = useState<any | null>(null);
    const [finance, setFinance] = useState<any | null>(null);
    const [kg, setKg] = useState<any | null>(null);
    const [consents, setConsents] = useState<any[]>([]);
    const [certs, setCerts] = useState<any[]>([]);
    const [docs, setDocs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const cid = ctx?.activeChildId;
    const currency = ctx?.tenant?.currency || "AZN";

    useEffect(() => {
        if (!cid) return;
        setLoading(true); setErr(null);
        (async () => {
            try {
                // Finance endpoint is now guarded server-side to payer role.
                // Guardians (non-payer) get 403 — we skip the request and
                // hide the tab below so they never trigger the error state.
                const financeReq = ctx?.isPayer
                    ? api.get("/portal/finance", { params: { child_person_id: cid } })
                    : Promise.resolve({ data: null });
                const [s, a, f, k, c, cr, dc] = await Promise.all([
                    api.get("/portal/sessions", { params: { child_person_id: cid, days: 21 } }),
                    api.get("/portal/attendance", { params: { child_person_id: cid } }),
                    financeReq,
                    api.get("/portal/kg-daily", { params: { child_person_id: cid } }).catch(() => ({ data: null })),
                    api.get("/portal/consents", { params: { child_person_id: cid } }).catch(() => ({ data: { items: [] } })),
                    api.get("/portal/certificates", { params: { child_person_id: cid } }).catch(() => ({ data: { items: [] } })),
                    api.get("/portal/documents", { params: { child_person_id: cid } }).catch(() => ({ data: { items: [] } })),
                ]);
                setSessions(s.data.items || []);
                setAttendance(a.data);
                setFinance(f.data);
                setKg(k.data);
                setConsents(c.data?.items || []);
                setCerts(cr.data?.items || []);
                setDocs(dc.data?.items || []);
            } catch (e: any) {
                setErr(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, [cid, ctx?.isPayer]);

    if (!ctx?.children?.length) {
        return (
            <div className="rounded-xl border p-8 text-center" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="portal-empty">
                <Baby size={40} style={{ color: "var(--text-muted)" }} className="mx-auto mb-3" />
                <div className="text-lg font-medium">{t("portal.no_children_title")}</div>
                <div className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{t("portal.no_children_body")}</div>
            </div>
        );
    }

    const tabs: { key: Tab; icon: ReactElement; label: string; testid: string }[] = [
        { key: "overview", icon: <CalendarDays size={16} />, label: t("portal.tab.overview"), testid: "portal-tab-overview" },
        { key: "attendance", icon: <ClipboardCheck size={16} />, label: t("portal.tab.attendance"), testid: "portal-tab-attendance" },
        // Phase 1B: Finance tab is only visible to payer users. Guardians
        // never see this option — the server would return 403 anyway.
        ...(ctx?.isPayer ? [{ key: "finance" as Tab, icon: <Wallet size={16} />, label: t("portal.tab.finance"), testid: "portal-tab-finance" }] : []),
        { key: "kg", icon: <Baby size={16} />, label: t("portal.tab.kg"), testid: "portal-tab-kg" },
        { key: "certs", icon: <Award size={16} />, label: t("portal.tab.certs"), testid: "portal-tab-certs" },
        { key: "docs", icon: <FileText size={16} />, label: t("portal.tab.docs"), testid: "portal-tab-docs" },
        { key: "consents", icon: <Shield size={16} />, label: t("portal.tab.consents"), testid: "portal-tab-consents" },
        { key: "profile", icon: <UserCog size={16} />, label: t("portal.tab.profile"), testid: "portal-tab-profile" },
    ];

    return (
        <div className="space-y-5" data-testid="portal-home">
            {/* Tab bar */}
            <div className="flex items-center gap-1 overflow-x-auto pb-1" role="tablist">
                {tabs.map((tt) => (
                    <button key={tt.key} data-testid={tt.testid} onClick={() => setTab(tt.key)} role="tab" aria-selected={tab === tt.key}
                        className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm shrink-0 transition-colors"
                        style={{ background: tab === tt.key ? "var(--selected-bg)" : "transparent", color: tab === tt.key ? "var(--text)" : "var(--text-muted)", minHeight: 40 }}>
                        {tt.icon}<span>{tt.label}</span>
                    </button>
                ))}
            </div>

            {loading && <div className="flex justify-center p-6"><Loader2 className="animate-spin" size={22} /></div>}
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="portal-error">{err}</div>}

            {/* Overview */}
            {tab === "overview" && !loading && (
                <div className="space-y-4" data-testid="portal-overview">
                    <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                        <h2 className="text-base font-semibold mb-3">{t("portal.upcoming_sessions")}</h2>
                        {sessions.length === 0 && <div className="text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div>}
                        <div className="space-y-2">
                            {sessions.slice(0, 8).map((s) => (
                                <div key={s.id} className="flex items-start gap-3 rounded-md border p-3" style={{ borderColor: "var(--border)" }} data-testid={`portal-session-${s.id}`}>
                                    <CalendarDays size={16} style={{ color: "var(--text-muted)" }} className="mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium">{s.group_name}</div>
                                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{s.scheduled_date} · {s.start_time}–{s.end_time}</div>
                                    </div>
                                    {s.status === "cancelled" && <span className="text-xs rounded-full px-2 py-0.5" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{t("session.status.cancelled")}</span>}
                                    {s.status === "moved" && <span className="text-xs rounded-full px-2 py-0.5" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }}>{t("session.status.moved")}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Attendance */}
            {tab === "attendance" && !loading && attendance && (
                <div className="space-y-4" data-testid="portal-attendance">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {(["present", "late", "excused_absence", "unexcused_absence"] as const).map((k) => (
                            <div key={k} className="rounded-xl border p-3" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                                <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t(`attendance.status.${k}`)}</div>
                                <div className="text-2xl font-semibold mt-1">{attendance.counts?.[k] || 0}</div>
                            </div>
                        ))}
                    </div>
                    <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-base font-semibold">{t("portal.month_summary")}</h2>
                            <div className="text-sm" style={{ color: "var(--text-muted)" }}>{t("person.att.pct")}: {attendance.attendance_pct ?? "—"}%</div>
                        </div>
                        <div className="space-y-1">
                            {attendance.sessions.slice(0, 20).map((r: any) => (
                                <div key={r.session_id} className="flex items-center justify-between text-sm py-1.5 border-b" style={{ borderColor: "var(--border)" }}>
                                    <div>{r.date} · {r.time}</div>
                                    <StatusPill status={r.status} t={t} />
                                </div>
                            ))}
                            {attendance.sessions.length === 0 && <div className="text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div>}
                        </div>
                    </div>
                </div>
            )}

            {/* Finance */}
            {tab === "finance" && !loading && finance && (
                <div className="space-y-4" data-testid="portal-finance">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="portal-total-debt">
                            <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("finance.debt")}</div>
                            <div className="text-2xl font-semibold mt-1" style={{ color: finance.total_debt_minor > 0 ? "var(--error-fg)" : "var(--text)" }}>
                                {formatMoney(finance.total_debt_minor, currency, i18n.language)}
                            </div>
                        </div>
                        <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                            <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("person.finance.credit_balance")}</div>
                            <div className="text-2xl font-semibold mt-1">{formatMoney(finance.credit_balance_minor, currency, i18n.language)}</div>
                        </div>
                    </div>
                    <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                        <h2 className="text-base font-semibold mb-3">{t("finance.invoices.title")}</h2>
                        <div className="space-y-2">
                            {finance.invoices.length === 0 && <div className="text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div>}
                            {finance.invoices.map((i: any) => (
                                <div key={i.id} className="flex items-center justify-between rounded-md border p-2.5 text-sm" style={{ borderColor: "var(--border)" }} data-testid={`portal-invoice-${i.id}`}>
                                    <div>
                                        <div className="font-medium">{i.invoice_number}</div>
                                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{i.due_date || "—"}</div>
                                    </div>
                                    <div className="text-right">
                                        <div>{formatMoney(i.total_minor, i.currency, i18n.language)}</div>
                                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t(`finance.invoice.status.${i.status}`)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                        <h2 className="text-base font-semibold mb-3">{t("finance.payments.title")}</h2>
                        <div className="space-y-2">
                            {finance.payments.length === 0 && <div className="text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div>}
                            {finance.payments.map((p: any) => (
                                <div key={p.id} className="flex items-center justify-between rounded-md border p-2.5 text-sm" style={{ borderColor: "var(--border)" }} data-testid={`portal-payment-${p.id}`}>
                                    <div>
                                        <div className="font-medium">{p.receipt_number}</div>
                                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{fmtDate(p.paid_at)} · {t(`finance.method.${p.method}`)}</div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-right font-medium">{formatMoney(p.amount_minor, p.currency, i18n.language)}</div>
                                        <Link to={`/portal/receipt/${p.id}`} className="text-xs" style={{ color: "var(--primary)" }} data-testid={`portal-receipt-link-${p.id}`}>{t("finance.receipt")}</Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Kindergarten daily */}
            {tab === "kg" && !loading && (
                <div className="space-y-4" data-testid="portal-kg">
                    {!kg && <div className="rounded-xl border p-6 text-sm" style={{ borderColor: "var(--border)", background: "var(--card)", color: "var(--text-muted)" }}>{t("portal.kg_not_enabled")}</div>}
                    {kg && (
                        <>
                            <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                                <h2 className="text-base font-semibold mb-2">{t("portal.kg.today")}</h2>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div><span style={{ color: "var(--text-muted)" }}>{t("kg.arrival")}: </span><span className="font-medium">{kg.attendance?.arrival_local || "—"}</span></div>
                                    <div><span style={{ color: "var(--text-muted)" }}>{t("kg.departure")}: </span><span className="font-medium">{kg.attendance?.departure_local || "—"}</span></div>
                                </div>
                            </div>
                            <div className="rounded-xl border p-4 space-y-2 text-sm" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                                <div><span style={{ color: "var(--text-muted)" }}>{t("kg.log.food")}: </span>{kg.log?.food_status ? t(`kg.food.${kg.log.food_status}`) : "—"}</div>
                                {kg.log?.food_notes && <div style={{ color: "var(--text-muted)" }}>{kg.log.food_notes}</div>}
                                <div><span style={{ color: "var(--text-muted)" }}>{t("kg.log.activity")}: </span>{kg.log?.activity_notes || "—"}</div>
                                <div><span style={{ color: "var(--text-muted)" }}>{t("kg.log.parent_notes")}: </span>{kg.log?.parent_notes || "—"}</div>
                            </div>
                        </>
                    )}
                </div>
            )}


            {/* Certificates — issued only, from /portal/certificates */}
            {tab === "certs" && !loading && (
                <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="portal-certs">
                    <h2 className="text-base font-semibold mb-3">{t("portal.certs_title")}</h2>
                    {certs.length === 0 && <div className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="portal-certs-empty">{t("portal.certs_empty")}</div>}
                    <div className="space-y-2">
                        {certs.map((c) => (
                            <div key={c.id} className="rounded-md border p-3 flex items-center gap-3" style={{ borderColor: "var(--border)" }} data-testid={`portal-cert-${c.cert_number}`}>
                                <Award size={18} style={{ color: "var(--primary)" }} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{c.program_name || "—"}</div>
                                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{c.cert_number} · {fmtDate(c.issued_at)}</div>
                                </div>
                                <a href={`/verify/${c.cert_number}`} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 shrink-0" style={{ color: "var(--primary)" }} data-testid={`portal-cert-verify-${c.cert_number}`}>{t("portal.cert_verify")}</a>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Documents — shared_with_parent=true only, auth-required download */}
            {tab === "docs" && !loading && (
                <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="portal-docs">
                    <h2 className="text-base font-semibold mb-3">{t("portal.docs_title")}</h2>
                    <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>{t("portal.docs_note")}</p>
                    {docs.length === 0 && <div className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="portal-docs-empty">{t("portal.docs_empty")}</div>}
                    <div className="space-y-2">
                        {docs.map((d) => (
                            <div key={d.id} className="rounded-md border p-3 flex items-center gap-3" style={{ borderColor: "var(--border)" }} data-testid={`portal-doc-${d.id}`}>
                                <FileText size={18} style={{ color: "var(--text-muted)" }} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{d.filename}</div>
                                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{d.mime_type} · {(d.size_bytes / 1024).toFixed(1)} KB · {fmtDate(d.created_at)}</div>
                                </div>
                                <button
                                    data-testid={`portal-doc-download-${d.id}`}
                                    onClick={async () => {
                                        try {
                                            const r = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
                                            const url = URL.createObjectURL(r.data);
                                            const link = document.createElement("a");
                                            link.href = url; link.download = d.filename;
                                            document.body.appendChild(link); link.click();
                                            link.remove(); URL.revokeObjectURL(url);
                                        } catch (e: any) { toast.error(formatApiError(e)); }
                                    }}
                                    className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium border shrink-0"
                                    style={{ background: "var(--input)", color: "var(--text)", borderColor: "var(--border)" }}>
                                    <Download size={12} />{t("portal.download")}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}


            {/* Consents */}
            {tab === "consents" && !loading && (
                <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="portal-consents">
                    <h2 className="text-base font-semibold mb-3">{t("portal.consents_title")}</h2>
                    {consents.length === 0 && <div className="text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div>}
                    <div className="space-y-2">
                        {consents.map((c) => (
                            <div key={c.id} className="flex items-center justify-between rounded-md border p-2.5 text-sm" style={{ borderColor: "var(--border)" }}>
                                <div>{String(t(`consent.${c.consent_type}`, c.consent_type))}</div>
                                <span className="text-xs rounded-full px-2 py-0.5" style={{ background: c.status === "granted" ? "var(--success-bg)" : "var(--error-bg)", color: c.status === "granted" ? "var(--success-fg)" : "var(--error-fg)" }}>{String(t(`consent.status.${c.status}`, c.status))}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Profile edit request */}
            {tab === "profile" && !loading && <ProfilePanel />}
        </div>
    );
}

function ProfilePanel() {
    const { t } = useTranslation();
    const [phone, setPhone] = useState("");
    const [address, setAddress] = useState("");
    const [email, setEmail] = useState("");
    const [requests, setRequests] = useState<any[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);
    const [err, setErr] = useState<string | null>(null);

    const load = async () => {
        try { const { data } = await api.get("/portal/profile-requests"); setRequests(data.items || []); } catch { /* ignore */ }
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        const changes: Record<string, string> = {};
        if (phone.trim()) changes.phone = phone.trim();
        if (address.trim()) changes.address = address.trim();
        if (email.trim()) changes.email = email.trim();
        if (!Object.keys(changes).length) { setErr(t("portal.profile.need_field")); return; }
        setSubmitting(true); setErr(null); setMsg(null);
        try {
            await api.post("/portal/profile-requests", { changes });
            setMsg(t("portal.profile.submitted"));
            setPhone(""); setAddress(""); setEmail("");
            await load();
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setSubmitting(false); }
    };

    return (
        <div className="space-y-4" data-testid="portal-profile">
            <div className="rounded-xl border p-4 space-y-3" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                <h2 className="text-base font-semibold">{t("portal.profile.request_title")}</h2>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>{t("portal.profile.request_desc")}</p>
                <label className="block text-sm">{t("people.phone")}
                    <input data-testid="portal-profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 w-full rounded-md border px-3 py-2 text-sm" style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                </label>
                <label className="block text-sm">{t("auth.email")}
                    <input data-testid="portal-profile-email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full rounded-md border px-3 py-2 text-sm" style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                </label>
                <label className="block text-sm">{t("person.address")}
                    <input data-testid="portal-profile-address" value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1 w-full rounded-md border px-3 py-2 text-sm" style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                </label>
                <button data-testid="portal-profile-submit" disabled={submitting} onClick={submit} className="rounded-md px-4 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>{t("portal.profile.submit")}</button>
                {msg && <div className="text-sm" style={{ color: "var(--success-fg)" }} data-testid="portal-profile-msg">{msg}</div>}
                {err && <div className="text-sm" style={{ color: "var(--error-fg)" }}>{err}</div>}
            </div>
            <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                <h3 className="text-sm font-semibold mb-2">{t("portal.profile.history")}</h3>
                {requests.length === 0 && <div className="text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div>}
                <div className="space-y-2">
                    {requests.map((r) => (
                        <div key={r.id} className="rounded-md border p-2.5 text-xs" style={{ borderColor: "var(--border)" }} data-testid={`portal-profile-request-${r.id}`}>
                            <div className="flex items-center justify-between">
                                <span>{fmtDateTime(r.created_at)}</span>
                                <span className="rounded-full px-2 py-0.5" style={{ background: r.status === "approved" ? "var(--success-bg)" : r.status === "rejected" ? "var(--error-bg)" : "var(--warning-bg)", color: r.status === "approved" ? "var(--success-fg)" : r.status === "rejected" ? "var(--error-fg)" : "var(--warning-fg)" }}>{t(`portal.profile.status.${r.status}`)}</span>
                            </div>
                            <div className="mt-1" style={{ color: "var(--text-muted)" }}>{Object.entries(r.changes || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
